import _, {partition} from 'lodash';

import {InboxModel} from '../../../../libs/shared/core/src/models/api/inboxModel';
import {ConversationBasesSelector} from '../../../../src/store/data/conversations/conversationsSelectors';
import {makeGetMoveTargetInboxes} from '../../../../src/store/data/inboxes/inboxesSelectors';
import {getIsInSandbox} from '../../../../src/store/data/teams/teamsSelectors';
import {createSelector} from '../../../helpers/reselectHelpers';

/*
 * Selectors.
 */

export function makeGetConversationInboxIds(getTargetConversations: ConversationBasesSelector<any>) {
  return createSelector([getTargetConversations], (conversations) => {
    const inboxIds = _(conversations)
      .map((c) => c.inboxIds)
      .flatten()
      .value();

    return new Set(inboxIds);
  });
}

export function makeGetPartitionedInboxes(
  getInboxes: ReturnType<typeof makeGetMoveTargetInboxes>,
  getConversationInboxIds: ReturnType<typeof makeGetConversationInboxIds>,
) {
  return createSelector([getInboxes, getConversationInboxIds], (inboxes, conversationInboxIds) =>
    partition(inboxes, (i) => conversationInboxIds.has(i.id)),
  );
}

export function makeGetSortedInboxes(getPartitionedInboxes: ReturnType<typeof makeGetPartitionedInboxes>) {
  return createSelector([getPartitionedInboxes], ([conversationInboxes, otherInboxes]) => [
    ...conversationInboxes,
    ...otherInboxes,
  ]);
}

export function makeGetConversationInboxes(
  getPartitionedInboxes: ReturnType<typeof makeGetPartitionedInboxes>,
) {
  return createSelector([getPartitionedInboxes], ([conversationInboxes]) => conversationInboxes);
}

// Prevent moving production conversations to the sandbox.
export function makeGetFilterSandboxPredicate(
  getConversationInboxes: ReturnType<typeof makeGetConversationInboxes>,
) {
  return createSelector([getConversationInboxes, getIsInSandbox], (conversationInboxes, isInSandbox) => {
    const isAlreadyInSandbox = conversationInboxes.some(isInSandbox);

    // Only allow moving to sandbox if conversation is already in the sandbox.
    return (inbox: InboxModel) => !isInSandbox(inbox) || isAlreadyInSandbox;
  });
}
